import { R4 } from "@ahryman40k/ts-fhir-types";
import dayjs from "dayjs";

import config from "@/config";
import { MimeType, Role, UnauthorizedError } from "@/global";
import { IDocumentReferenceExtended } from "@/store/modules/document";
import { hexToBytes } from "@/utils";

export async function fetchDocuments(
  authorization: string,
  url: string | undefined = undefined
): Promise<R4.IBundle> {
  let requestUrl = url ? url : config.r4.documents.referenceEndpoint;

  if (!url) {
    const searchParams = new URLSearchParams();
    searchParams.append("status", "current,superseded");
    searchParams.append("_revinclude", "List:item");
    requestUrl = `${requestUrl}?${searchParams.toString()}`;
  }

  const response = await window.fetch(requestUrl, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: authorization,
    },
  });

  if (!response.ok) {
    if (response.status === 403) {
      throw new UnauthorizedError("toasts.auth.noEPDAccess");
    } else {
      throw new Error("Response not OK");
    }
  }

  return (await response.json()) as R4.IBundle;
}

export async function fetchDocumentReference(
  id: string,
  authorization: string
): Promise<R4.IDocumentReference> {
  const response = await window.fetch(`${config.r4.documents.referenceEndpoint}/${id}`, {
    method: "GET",
    headers: {
      Authorization: authorization,
    },
  });

  if (!response.ok) {
    if (response.status === 403) {
      throw new UnauthorizedError("toasts.auth.noEPDAccess");
    } else {
      throw new Error("Response not OK");
    }
  }

  return (await response.json()) as R4.IDocumentReference;
}

export async function fetchDocumentReferences(
  query: string = "",
  authorization: string
): Promise<R4.IDocumentReference[]> {
  const url = `${config.r4.documents.referenceEndpoint}?${query}`;
  const response = await window.fetch(encodeURI(url), {
    method: "GET",
    headers: {
      Authorization: authorization,
    },
  });

  if (!response.ok) {
    throw new Error("Failed to fetch document references");
  }

  const bundle = (await response.json()) as R4.IBundle;
  return (bundle.entry?.map((entry) => entry.resource) || []) as R4.IDocumentReference[];
}

export async function fetchFhirDocumentsByUrl(
  urls: string[],
  authorization: string
): Promise<R4.IBundle | null> {
  if (urls.length === 0) {
    return null;
  }

  const response = await window.fetch(config.r4.base, {
    method: "POST",
    headers: {
      Authorization: authorization,
      Accept: MimeType.ApplicationFHIRJSON,
      "Content-Type": MimeType.ApplicationFHIRJSON,
    },
    body: JSON.stringify({
      resourceType: "Bundle",
      type: "batch",
      entry: urls.map((url) => ({
        request: {
          method: "GET",
          url,
        },
      })),
    }),
  });

  if (!response.ok) {
    throw new Error("Failed to fetch documents");
  }

  return (await response.json()) as R4.IBundle;
}

export async function deleteDocument(entry: IDocumentReferenceExtended, authorization: string) {
  const documentReference: R4.IDocumentReference = {
    resourceType: "DocumentReference",
    id: crypto.randomUUID(),
    extension: [
      {
        url: "http://fhir.ch/ig/ch-epr-mhealth/StructureDefinition/ch-ext-deletionstatus",
        valueCoding: {
          system:
            "http://fhir.ch/ig/ch-epr-mhealth/CodeSystem/ch-ehealth-codesystem-deletionstatus",
          code: "deletionRequested",
        },
      },
    ],
    content: [],
  };

  const submissionSet = generateSubmissionSet(documentReference);

  const bundle: R4.IBundle = {
    resourceType: "Bundle",
    meta: {
      profile: ["https://api.phellowseven.com/fhir/StructureDefinition/IHE.MHD.Metadata.Update"],
    },
    type: R4.BundleTypeKind._transaction,
    entry: [],
  };

  bundle.entry?.push({
    fullUrl: "List/" + submissionSet.id,
    resource: submissionSet,
    request: {
      method: R4.Bundle_RequestMethodKind._post,
      url: "List/" + submissionSet.id,
    },
  });

  (documentReference as R4.IDocumentReference).id = entry?.id;

  bundle.entry?.push({
    fullUrl: "DocumentReference/" + entry?.id,
    resource: documentReference as R4.IDocumentReference,
    request: {
      method: R4.Bundle_RequestMethodKind._put,
      url: "DocumentReference/" + entry?.id,
    },
  });

  const url = config.r4.documents.upload;

  const response = await window.fetch(encodeURI(url), {
    method: "POST",
    body: JSON.stringify(bundle),
    headers: {
      Authorization: authorization,
      "Content-Type": "application/json",
    },
  });

  if (!response.ok) {
    throw new Error("Failed to delete document");
  }
}

export function generateSubmissionSet(
  documentReference: R4.IDocumentReference,
  role: Role = Role.Patient
): R4.IList {
  const masterIdentifier: R4.IIdentifier = (() => {
    const suffix = new Uint8Array(hexToBytes(crypto.randomUUID().replaceAll("-", "")));

    return {
      value: "urn:oid:2.25." + suffix.join(""),
      system: "urn:ietf:rfc:3986",
    };
  })();

  const identifier: R4.IIdentifier[] = [
    masterIdentifier,
    {
      value: "urn:uuid:" + crypto.randomUUID(),
      system: "urn:ietf:rfc:3986",
    },
  ];

  const id = crypto.randomUUID();

  const status = R4.ListStatusKind._current;

  const type: R4.IExtension = {
    url: "http://profiles.ihe.net/ITI/MHD/StructureDefinition/ihe-designationType",
    valueCodeableConcept: {
      coding: [
        {
          code: "71388002",
          system: "2.16.840.1.113883.6.96",
          display: "Procedure (procedure)",
        },
      ],
    },
  };
  const date = dayjs().format();

  const source: R4.IReference = {
    reference: "#1",
  };

  const authorRole: R4.IPractitionerRole = {
    id: `1`,
    resourceType: "PractitionerRole",
    code: [
      {
        coding: [
          {
            code: role,
            system: "2.16.756.5.30.1.127.3.10.6",
          },
        ],
      },
    ],
  };

  const contained: R4.IResourceList[] = [authorRole];

  const entry: R4.IList_Entry[] = [
    {
      item: {
        reference: documentReference.id,
      },
    },
  ];

  const list: R4.IList = {
    resourceType: "List",
    extension: [type],
    code: {
      coding: [
        {
          system: "http://profiles.ihe.net/ITI/MHD/CodeSystem/MHDlistTypes",
          code: "submissionset",
        },
      ],
    },
    identifier,
    id,
    status,
    date,
    source,
    contained,
    entry,
  };

  return list;
}

export async function updateDocumentStatus(
  entry: R4.IDocumentReference,
  status: R4.DocumentReferenceStatusKind,
  authorization: string
) {
  const documentReference: R4.IDocumentReference = {
    resourceType: "DocumentReference",
    id: crypto.randomUUID(),
    status,
    content: [],
  };

  const submissionSet = generateSubmissionSet(documentReference);

  const bundle: R4.IBundle = {
    resourceType: "Bundle",
    meta: {
      profile: ["https://api.phellowseven.com/fhir/StructureDefinition/IHE.MHD.Metadata.Update"],
    },
    type: R4.BundleTypeKind._transaction,
    entry: [],
  };

  bundle.entry?.push({
    fullUrl: "List/" + submissionSet.id,
    resource: submissionSet,
    request: {
      method: R4.Bundle_RequestMethodKind._post,
      url: "List/" + submissionSet.id,
    },
  });

  (documentReference as R4.IDocumentReference).id = entry?.id;

  bundle.entry?.push({
    fullUrl: "DocumentReference/" + entry?.id,
    resource: documentReference as R4.IDocumentReference,
    request: {
      method: R4.Bundle_RequestMethodKind._put,
      url: "DocumentReference/" + entry?.id,
    },
  });

  const url = config.r4.documents.upload;

  const response = await window.fetch(encodeURI(url), {
    method: "POST",
    body: JSON.stringify(bundle),
    headers: {
      Authorization: authorization,
      "Content-Type": "application/json",
    },
  });

  if (!response.ok) {
    throw new Error("Failed to update document status");
  }
}

export async function convertFhirJsonToXml(
  fhirJson: string,
  authorization: string
): Promise<string> {
  const response = await fetch(config.r4.documents.convert, {
    method: "POST",
    headers: {
      Authorization: authorization,
      Accept: "application/fhir+xml",
      "Content-Type": "application/fhir+json",
    },
    body: fhirJson,
  });
  return await response.text();
}

export async function convertXml(
  xml: string,
  sourceMimeType: MimeType.ApplicationFHIRXML | MimeType.TextXML,
  sourceFormatCode: string,
  targetMimeType: string,
  authorization: string,
  language: string | undefined
): Promise<Blob> {
  let conversionUrl = config.eprServices.conversion;
  if (language) {
    conversionUrl += `?locale=${language}`;
  }

  const response = await fetch(conversionUrl, {
    method: "POST",
    headers: {
      Authorization: authorization,
      "Content-Type": `${sourceMimeType}; format=${sourceFormatCode}`,
      Accept: targetMimeType,
    },
    body: xml,
  });
  return await response.blob();
}

export async function uploadDocument(
  documentRef: R4.IDocumentReference,
  content: Blob,
  role: Role,
  authorization: string
): Promise<R4.IBundle> {
  const list = generateSubmissionSet(documentRef, role);
  const bundle: R4.IBundle = {
    resourceType: "Bundle",
    meta: {
      profile: [
        "http://profiles.ihe.net/ITI/MHD/StructureDefinition/IHE.MHD.Comprehensive.ProvideBundle",
      ],
    },
    type: R4.BundleTypeKind._transaction,
    entry: [],
  };

  bundle.entry!.push({
    fullUrl: list.id,
    resource: list,
  });

  bundle.entry!.push({
    fullUrl: documentRef.id,
    resource: documentRef,
  });

  const url = config.r4.documents.upload;

  const formData = new FormData();
  formData.append("bundle", new Blob([JSON.stringify(bundle)], { type: "application/fhir+json" }));

  formData.append(
    documentRef.content[0].attachment.url!,
    new Blob([content!], { type: documentRef.content[0].attachment.contentType! })!
  );

  const response = await window.fetch(url, {
    method: "POST",
    body: formData,
    headers: {
      authorization,
    },
  });

  if (!response.ok) {
    throwErrorMessage(response);
  }

  return (await response.json()) as R4.IBundle;
}

function throwErrorMessage(response: Response) {
  try {
    const body = response.json() as any;
    if (typeof body === "object" && body.resourceType === "OperationOutcome") {
      const operationOutcome = body as R4.IOperationOutcome;
      if (operationOutcome.issue?.length > 0) {
        const issue = operationOutcome.issue[0];
        throw new Error(issue.diagnostics);
      }
    }
  } catch (e) {
    throw new Error(response.statusText);
  }
  throw new Error(response.statusText);
}

export async function downloadDocument(url: string, authorization: string): Promise<Blob> {
  const response = await window.fetch(url, {
    method: "GET",
    headers: {
      Authorization: authorization,
    },
  });

  if (!response.ok) {
    throwErrorMessage(response);
  }

  return await response.blob();
}
